// ── Filter Bar — Evergreen header strip ──

import type { ReactNode } from "react";
import type {
  NodeType,
  FilterState,
  GraphDomainFacets,
  GraphFacetFilterDef,
} from "../types/graph";
import DomainFacetSelect from "./DomainFacetSelect";

interface FilterBarProps {
  filter: FilterState;
  onChange: (next: FilterState) => void;
  breadcrumb: Array<{ label: string; type: NodeType }>;
  onBreadcrumbClick: (index: number) => void;
  facets?: GraphDomainFacets;
  facetFilters?: GraphFacetFilterDef[];
  children?: ReactNode;
}

const nodeTypes: NodeType[] = ["site", "campaign", "area", "element", "inspection"];

export default function FilterBar({
  filter,
  onChange,
  breadcrumb,
  onBreadcrumbClick,
  facets,
  facetFilters,
  children,
}: FilterBarProps) {
  return (
    <div
      className="fixed top-0 left-0 right-0 z-30 h-14 flex items-center gap-4 px-6
                  bg-fl-evergreen border-b border-fl-forest"
    >
      {/* Logo — FL Calibration Mark + wordmark */}
      <div className="flex items-center gap-2.5 mr-4">
        <svg width="28" height="28" viewBox="0 0 175.27 175.27" className="flex-shrink-0">
          <path fill="#e9e1b5" d="M87.61,22.45c-35.74,0-64.82,29.08-64.82,64.82s29.08,64.82,64.82,64.82,64.82-29.08,64.82-64.82S123.35,22.45,87.61,22.45M87.61,163.49c-42.03,0-76.23-34.19-76.23-76.22S45.58,11.04,87.61,11.04s76.23,34.19,76.23,76.23-34.19,76.22-76.23,76.22"/>
          <path fill="#e9e1b5" d="M87.61,44.78c-23.43,0-42.49,19.06-42.49,42.49s19.06,42.49,42.49,42.49,42.49-19.06,42.49-42.49-19.06-42.49-42.49-42.49M87.61,141.17c-29.72,0-53.9-24.18-53.9-53.9s24.18-53.9,53.9-53.9,53.9,24.18,53.9,53.9-24.18,53.9-53.9,53.9"/>
        </svg>
        <div className="flex items-baseline gap-2">
          <span className="text-fl-sand font-light text-sm tracking-wider">fraserlever</span>
          <span className="text-fl-sand/40 text-xs">|</span>
          <span className="text-white font-semibold text-sm tracking-wide">NEXUS</span>
        </div>
      </div>

      {/* Breadcrumb */}
      <nav className="flex items-center gap-1 text-sm">
        <button
          onClick={() => onBreadcrumbClick(-1)}
          className="text-fl-sand-80 hover:text-white transition-colors"
        >
          All Sites
        </button>
        {breadcrumb.map((crumb, i) => (
          <span key={i} className="flex items-center gap-1">
            <span className="text-fl-forest-60">/</span>
            <button
              onClick={() => onBreadcrumbClick(i)}
              className="text-fl-sand-80 hover:text-white transition-colors"
            >
              {crumb.label}
            </button>
          </span>
        ))}
      </nav>

      {/* View switcher */}
      {children}

      {/* Spacer */}
      <div className="flex-1" />

      {/* Registry-aligned domain facet filters (Phase 7) */}
      {facets && facetFilters && facetFilters.length > 0 && (
        <div className="hidden lg:flex items-center gap-2">
          {facetFilters.map((def) => {
            const options = facets[def.facetKey] ?? [];
            if (!options.length) return null;
            return (
              <DomainFacetSelect
                key={def.recordField}
                label={def.label}
                options={options}
                selected={filter[def.filterKey] ?? []}
                onChange={(next) => onChange({ ...filter, [def.filterKey]: next })}
              />
            );
          })}
        </div>
      )}

      {/* Severity range */}
      <div className="flex items-center gap-2 text-xs text-fl-sand-80">
        <span>Severity</span>
        <input
          type="range"
          min={0}
          max={5}
          step={0.5}
          value={filter.minSeverity ?? 0}
          onChange={(e) =>
            onChange({ ...filter, minSeverity: parseFloat(e.target.value) })
          }
          className="w-20 accent-fl-leaf"
        />
        <span className="text-white w-6 text-right">
          {(filter.minSeverity ?? 0).toFixed(1)}
        </span>
      </div>

      {/* Node type toggles */}
      <div className="flex items-center gap-1">
        {nodeTypes.map((nt) => {
          const active = !(filter.hideTypes ?? []).includes(nt);
          return (
            <button
              key={nt}
              onClick={() => {
                const current = filter.hideTypes ?? [];
                const next = active
                  ? [...current, nt]
                  : current.filter((t: NodeType) => t !== nt);
                onChange({ ...filter, hideTypes: next });
              }}
              className={`px-2 py-0.5 rounded text-xs capitalize transition-all
                ${active
                  ? "bg-fl-leaf/25 text-white border border-fl-leaf/40"
                  : "bg-fl-forest/40 text-fl-evergreen-40 border border-fl-forest-60/30"
                }`}
            >
              {nt}
            </button>
          );
        })}
      </div>
    </div>
  );
}
